import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import { searchFormValueChanged } from '@planone/bau365/data-access-search';
import { loadUserHistory } from '@planone/bau365/data-access-user';
import { map, tap, withLatestFrom } from 'rxjs';

import {
  inputSearchTerm,
  loadMore,
  openPage,
  performSearch,
  selectLastViewedChannel,
  selectRootChannel,
  selectSubscribedChannel,
} from './newsfeed-page.actions';

import { getSearchTermFragment } from './newsfeed-page.selectors';

import { loadQuicklinks } from '../newsfeed-quicklinks/newsfeed-quicklinks.actions';
import { loadMoreContent } from '../newsfeed-results/newsfeed-results.actions';
import { getSource } from '../newsfeed-results/newsfeed-results.selectors';

@Injectable()
export class NewsfeedPageEffects {
  loadMoreContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMore),
      withLatestFrom(this.store.pipe(select(getSource))),
      map(([_, source]) => loadMoreContent({ source }))
    )
  );

  inputSearchTerm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(inputSearchTerm),
      map(({ searchTermFragment }) => searchFormValueChanged({ searchTermFragment }))
    )
  );

  loadQuicklinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openPage),
      map(() => loadQuicklinks())
    )
  );

  loadUserHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openPage),
      map(() => loadUserHistory())
    )
  );

  performSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(performSearch),
        withLatestFrom(this.store.pipe(select(getSearchTermFragment)), this.fairsFacade.insightsId$),
        tap(([_, searchTerm, insightsId]) =>
          this.router.navigate(['fairs', insightsId, 'search'], { queryParams: { searchTerm } })
        )
      ),
    {
      dispatch: false,
    }
  );

  selectRootChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(selectRootChannel),
        tap(() => this.router.navigate(['newsfeed']))
      ),
    { dispatch: false }
  );

  selectSubscribedChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(selectSubscribedChannel),
        tap(({ id, fairId }) => this.router.navigate(['newsfeed', 'subscription', fairId, id]))
      ),
    {
      dispatch: false,
    }
  );

  selectLastViewedChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(selectLastViewedChannel),
        tap(() => this.router.navigate(['newsfeed', 'history']))
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private fairsFacade: DataAccessFairsFacade,
    private router: Router
  ) {}
}
